import { Box, Grid, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  FEATURED_ROUTE_CARDS,
  buildSearchUrl,
  resolveFeaturedCard,
} from "../data/featuredNetwork";
import { SectionTitle } from "./layout";

/**
 * Flagship OD pair photo cards → one-click search (from + to).
 */
const PopularTrips = () => {
  const { t } = useTranslation("common");
  const navigate = useNavigate();

  const openRoute = (fromName, toName) => {
    navigate(buildSearchUrl({ fromName, toName }));
    window.setTimeout(() => {
      document
        .getElementById("home-trip-search")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
  };

  const cards = FEATURED_ROUTE_CARDS.map(resolveFeaturedCard).filter(Boolean);

  return (
    <Box sx={{ width: "100%", pt: { xs: 2, sm: 3 }, pb: { xs: 1, sm: 1.5 } }}>
      <SectionTitle
        title={t("popularTrips.title")}
        subtitle={t("popularTrips.subtitle")}
        sx={{ mb: 3, "& .MuiTypography-body1": { maxWidth: 640, mx: "auto", px: 1 } }}
      />

      <Grid container spacing={2}>
        {cards.map((card) => (
          <Grid item xs={12} sm={6} md={3} key={`${card.fromKey}-${card.toKey}`}>
            <Box
              component="button"
              type="button"
              onClick={() => openRoute(card.from.name, card.to.name)}
              sx={{
                width: "100%",
                textAlign: "left",
                cursor: "pointer",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                bgcolor: "background.paper",
                p: 0,
                overflow: "hidden",
                font: "inherit",
                color: "inherit",
                transition: "border-color 0.2s, transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  borderColor: "primary.main",
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
                  "& .city-photo": { transform: "scale(1.04)" },
                  "& .find-ticket-cta": {
                    bgcolor: "primary.dark",
                  },
                },
                "&:focus-visible": {
                  outline: "2px solid",
                  outlineColor: "primary.main",
                  outlineOffset: 2,
                },
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  height: { xs: 150, sm: 170 },
                  overflow: "hidden",
                }}
              >
                {card.image ? (
                  <Box
                    className="city-photo"
                    component="img"
                    src={card.image}
                    alt={`${t(`popularTrips.cities.${card.fromKey}`)} — ${t(`popularTrips.cities.${card.toKey}`)}`}
                    loading="lazy"
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                      transition: "transform 0.35s ease",
                    }}
                  />
                ) : (
                  <Box
                    className="city-photo"
                    sx={{
                      width: "100%",
                      height: "100%",
                      bgcolor: "grey.300",
                      transition: "transform 0.35s ease",
                    }}
                  />
                )}
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    background:
                      "linear-gradient(to top, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.15) 55%, transparent 100%)",
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    left: 14,
                    bottom: 12,
                    right: 14,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      color: "#fff",
                      fontWeight: 700,
                      textShadow: "0 1px 4px rgba(0,0,0,0.4)",
                      lineHeight: 1.25,
                    }}
                  >
                    {t(`popularTrips.cities.${card.fromKey}`)} —{" "}
                    {t(`popularTrips.cities.${card.toKey}`)}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "rgba(255,255,255,0.9)" }}
                  >
                    {t(`popularTrips.countries.${card.to.countryKey}`)}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ p: 2 }}>
                <Box
                  className="find-ticket-cta"
                  aria-hidden
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                    py: 1,
                    px: 2,
                    borderRadius: 1,
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                    fontWeight: 700,
                    fontSize: "0.875rem",
                    lineHeight: 1.75,
                    transition: "background-color 0.2s",
                  }}
                >
                  {t("popularTrips.findTicket")}
                </Box>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default PopularTrips;
